import { ActionBar } from "$components/action-bar/action-bar";
import { useRegisterActionBar } from "$components/action-bar/action-bar-offset";
import type { ActionItem } from "$components/action-bar/action-item";
import {
  AllergiesIcon,
  ConsultationIcon,
  DispenseIcon,
  InvestigationIcon,
  InvoiceIcon,
  UploadFileIcon,
  VitalsIcon,
} from "$components/icons";
import {
  type EventName,
  useAnalytics,
} from "$global-providers/analytics-provider/provider";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useUser } from "$global-providers/user-provider";
import privateGuard from "$routes/_lib/private-guard";
import { linkToPatientInvestigationsNew } from "$routes/patient/[:patientId]/_lib/investigations/links";
import RequestedCountBubble from "$routes/patient/[:patientId]/_lib/investigations/requested-count-bubble";
import useCreateInvoice from "$routes/patient/[:patientId]/_lib/invoices/components/hooks/useCreateInvoice";
import { linkToPatientCreateANC } from "$routes/patient/[:patientId]/antenatal-care/links";
import { linkToNewConsultation } from "$routes/patient/[:patientId]/consultation/links";
import { linkToPatientDetails } from "$routes/patient/[:patientId]/links";
import { linkToDispensePrescriptionOutpatientWithParams } from "$routes/patient/[:patientId]/prescriptions/links";
import { linkToNewReferral } from "$routes/patient/[:patientId]/referrals/links";
import { linkToNewVaccination } from "$routes/patient/[:patientId]/vaccinations/links";
import {
  Add as AddIcon,
  PregnantWoman as AntenatalCareIcon,
  Send as ReferralIcon,
  Colorize as VaccinationIcon,
} from "@material-ui/icons";
import { Role } from "elephant-web/gql";
import { useCallback, useRef } from "react";
import { FormattedMessage } from "react-intl";
import {
  type NavigateFunction,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { v4 } from "uuid";
import { VaccinationInitiatedFrom } from "../vaccinations/_lib/analytics";
import { usePatientHistoryFileUpload } from "./patient-history-file-upload";
import { useVaccinationsService } from "./vaccinations-service/provider";

const CHILD_MAX_BUTTONS = 5;
const ADULT_MAX_BUTTONS = 4;
const RequestedInvestigationsBubble = privateGuard([Role.Technician])(
  ({ patientId }: { patientId: string }) => (
    <RequestedCountBubble patientId={patientId} />
  ),
);

type ButtonConfig = {
  id: string;
  defaultMessage: string;
  type: "primary-add" | "secondary-add" | "secondary";
  // Inline buttons share a generic "+", so each action carries a distinct glyph
  // for the mobile SpeedDial where icon-only recognition matters.
  fabIcon: JSX.Element;
  to?: string;
  onClick?: (e: React.MouseEvent) => void;
};

// Everything a config's handler needs lives at the call site; the factory
// closes over it so each `onClick` is a plain `(e) => void` rather than a
// leaky multi-argument signature.
type ButtonConfigDeps = {
  navigate: NavigateFunction;
  params: URLSearchParams;
  trackEvent: (event: EventName, params: Record<string, string>) => void;
  trackingId: string;
  createNewInvoice: (e: React.MouseEvent) => Promise<void>;
};

const makeButtonConfigs = ({
  navigate,
  params,
  trackEvent,
  trackingId,
  createNewInvoice,
}: ButtonConfigDeps): ButtonConfig[] => [
  {
    id: "patientDetails.newConsultationBtn",
    defaultMessage: "Consultation",
    type: "primary-add",
    fabIcon: <ConsultationIcon />,
    to: linkToNewConsultation(),
  },
  {
    id: "patientDetails.vitalsBtn",
    defaultMessage: "Vitals",
    type: "secondary-add",
    fabIcon: <VitalsIcon />,
    onClick: () => {
      navigate({
        pathname: linkToPatientInvestigationsNew(),
        search: `?${params.toString()}&investigation=vitals&investigationLoinc=85353-1`,
      });
    },
  },
  {
    id: "patientDetails.vaccinationBtn",
    defaultMessage: "Vaccinations",
    type: "secondary-add",
    fabIcon: <VaccinationIcon />,
    onClick: () => {
      trackEvent("Vaccination Started", {
        patientId: params.get("patientUuid"),
        initiatedFrom: VaccinationInitiatedFrom.BOTTOM_BAR,
        trackingId,
      });
      navigate(
        {
          pathname: linkToNewVaccination(),
          search: `?${params.toString()}`,
        },
        {
          state: {
            trackingId,
          },
        },
      );
    },
  },
  {
    id: "patientDetails.addInvestigationBtn",
    defaultMessage: "Investigation",
    type: "secondary-add",
    fabIcon: <InvestigationIcon />,
    to: linkToPatientInvestigationsNew(),
  },
  {
    id: "patientDetails.dispenseBtn",
    defaultMessage: "Dispense",
    type: "secondary",
    fabIcon: <DispenseIcon />,
    to: linkToDispensePrescriptionOutpatientWithParams(),
  },
  {
    id: "patientDetails.allergiesBtn",
    defaultMessage: "Allergies",
    type: "secondary",
    fabIcon: <AllergiesIcon />,
    onClick: () => {
      navigate({
        pathname: linkToPatientInvestigationsNew(),
        search: `?${params.toString()}&investigation=allergy&investigationLoinc=95763-9`,
      });
    },
  },
  {
    id: "patientDetails.antenatalCareBtn",
    defaultMessage: "Antenatal Care",
    type: "secondary",
    fabIcon: <AntenatalCareIcon />,
    onClick: () => {
      navigate({
        pathname: linkToPatientCreateANC(),
        search: params.toString(),
      });
    },
  },
  {
    id: "patientDetails.invoiceBtn",
    defaultMessage: "Invoice",
    type: "primary-add",
    fabIcon: <InvoiceIcon />,
    onClick: (e) => createNewInvoice(e),
  },
  {
    id: "patientDetails.referralBtn",
    defaultMessage: "Referral",
    type: "secondary-add",
    fabIcon: <ReferralIcon />,
    to: linkToNewReferral(),
  },
];

// Sort buttons according to the role-specific order
const roleButtonOrder: Record<Role, string[]> = {
  [Role.Admin]: ["patientDetails.invoiceBtn"],
  [Role.Doctor]: [
    "patientDetails.newConsultationBtn",
    "patientDetails.vitalsBtn",
    "patientDetails.vaccinationBtn",
    "patientDetails.addInvestigationBtn",
    "patientDetails.dispenseBtn",
    "patientDetails.allergiesBtn",
    "patientDetails.antenatalCareBtn",
    "patientDetails.invoiceBtn",
  ],
  [Role.Nurse]: [
    "patientDetails.newConsultationBtn",
    "patientDetails.vitalsBtn",
    "patientDetails.vaccinationBtn",
    "patientDetails.addInvestigationBtn",
    "patientDetails.dispenseBtn",
    "patientDetails.allergiesBtn",
    "patientDetails.antenatalCareBtn",
    "patientDetails.invoiceBtn",
  ],
  [Role.Pharmacist]: [
    "patientDetails.dispenseBtn",
    "patientDetails.invoiceBtn",
  ],
  [Role.CashOfficer]: ["patientDetails.invoiceBtn"],
  [Role.Technician]: [
    "patientDetails.addInvestigationBtn",
    "patientDetails.invoiceBtn",
  ],
  [Role.Dispenser]: ["patientDetails.dispenseBtn", "patientDetails.invoiceBtn"],
  [Role.Root]: [],
  [Role.DataExporter]: [],
  [Role.Elephant]: [],
  [Role.Clinic]: [],
  [Role.Sonographer]: [
    "patientDetails.newConsultationBtn",
    "patientDetails.vitalsBtn",
    "patientDetails.vaccinationBtn",
    "patientDetails.addInvestigationBtn",
    "patientDetails.allergiesBtn",
    "patientDetails.antenatalCareBtn",
    "patientDetails.referralBtn",
  ],
  [Role.Performer]: [],
  [Role.Reviewer]: [],
  [Role.QaAdmin]: [],
  [Role.ProgramAdmin]: [],
};

export const PatientDetailsActionBar: FC<{
  patientId: string;
  className?: string;
}> = ({ patientId, className }) => {
  const { trackEvent } = useAnalytics();
  const trackingId = v4();
  const user = useUser();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [patientFileUploadFlag] = useFeatureFlag(
    featureFlags.patientFileUpload,
  );
  const [vaccinationFlag] = useFeatureFlag(
    featureFlags.immunisations.addVaccineFlow,
  );
  // V2 invoices tab carries its own low-weight "New invoice" footer button,
  // so the action-bar entry collapses when the same flag that routes to V2
  // (`rxdb-billing`) is on.
  const [rxdbBillingFlag] = useFeatureFlag(featureFlags.billing.rxdbBilling);
  const { getIsChildPatient } = useVaccinationsService();
  const isChild = getIsChildPatient();

  const actionBarRef = useRef<HTMLElement>(null);
  useRegisterActionBar(actionBarRef);

  const { openFilePicker, fileInput } = usePatientHistoryFileUpload(patientId);

  const { createInvoice } = useCreateInvoice(patientId);
  const createNewInvoice = useCallback(
    async (e: React.MouseEvent) => {
      if (e) e.preventDefault();
      try {
        const { data } = await createInvoice();
        if (!data) return; // allow onError of useCreateInvoice to inform the user
        navigate(
          linkToPatientDetails({
            patientUuid: patientId,
            invoiceId: data.createInvoice._id,
          }),
        );
      } catch (_err) {
        // allow onError of useCreateInvoice to inform the user
      }
    },
    [createInvoice, navigate, patientId],
  );

  const buttonConfigs = makeButtonConfigs({
    navigate,
    params,
    trackEvent,
    trackingId,
    createNewInvoice,
  });

  const buttonOrder = user.role ? roleButtonOrder[user.role] : [];

  const availableButtonsForRole = buttonConfigs
    .filter((btn) => buttonOrder.includes(btn.id))
    .filter(
      // V2 invoices tab carries the "New invoice" footer button, so when
      // rxdb-billing is on the prominent action-bar entry is hidden.
      (btn) => !(rxdbBillingFlag && btn.id === "patientDetails.invoiceBtn"),
    )
    .filter(
      // The Vaccinations action is the one entry gated behind the immunisations
      // feature flag (it opens the still-flagged vaccination flow). The rest of
      // the bar always renders.
      (btn) =>
        !(!vaccinationFlag && btn.id === "patientDetails.vaccinationBtn"),
    )
    .sort((a, b) => {
      if (!isChild) {
        // Vaccination button should be last button for adults.
        // Could make this more extensible by having adult/child button configs
        // if there are more button ordering differences for adults/children
        if (a.id === "patientDetails.vaccinationBtn") {
          return 1;
        }
        if (b.id === "patientDetails.vaccinationBtn") {
          return -1;
        }
      }

      const aIndex = buttonOrder.indexOf(a.id);
      const bIndex = buttonOrder.indexOf(b.id);
      return aIndex - bIndex;
    });

  // Hide the action bar completely if no buttons are available for this role
  if (availableButtonsForRole.length === 0) {
    return null;
  }

  const maxButtonsToShow = isChild ? CHILD_MAX_BUTTONS : ADULT_MAX_BUTTONS;

  const actions: ActionItem[] = availableButtonsForRole.map((config) => {
    const { to, onClick } = config;
    return {
      id: config.id,
      defaultMessage: config.defaultMessage,
      variant: config.type === "primary-add" ? "primary" : "secondary",
      icon: config.type.includes("add") ? <AddIcon /> : undefined,
      fabIcon: config.fabIcon,
      onClick:
        onClick ??
        (to
          ? () => navigate({ pathname: to, search: `?${params.toString()}` })
          : undefined),
    };
  });

  if (patientFileUploadFlag) {
    actions.push({
      id: "patient-history.button.upload",
      defaultMessage: "Upload document",
      icon: <UploadFileIcon />,
      fabIcon: <UploadFileIcon />,
      onClick: openFilePicker,
    });
  }

  return (
    <>
      {patientFileUploadFlag && fileInput}
      <ActionBar
        className={className}
        containerRef={actionBarRef}
        actions={actions}
        maxInline={maxButtonsToShow}
        moreActionsLabel={
          <FormattedMessage
            id="patientDetails.moreActions"
            defaultMessage="More actions"
          />
        }
        trailing={<RequestedInvestigationsBubble patientId={patientId} />}
      />
    </>
  );
};
